import { Parser } from "json2csv";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../parts/SvgIcon";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { getUserAccess } from "../../../utils/organization-helpers";
import { getFullName } from "../../../utils/personal-records";
import RangeContainerComponent from "../../athlete-portal/calendar/readiness/RangeContainerComponent";
import ChallengeCharts from "./components/ChallengeCharts";
import ChallengePhotoCompareModal from "./components/ChallengePhotoCompareModal";
import ChallengeTable from "./components/ChallengeTable";
import CustomizeTableModal from "./components/CustomizeTableModal";
import LeaderCards from "./components/LeaderCards";
import MemberPreviewTable from "./components/MemberPreviewTable";
import PublishStrip from "./components/PublishStrip";

const DEFAULT_COLUMNS = new Set([
  "workouts",
  "programTeam",
  "completionRate",
  "avgDailySteps",
  "habitsAdherence",
  "bodyweight",
  "photos",
]);
const DEFAULT_MEASUREMENTS = new Set(["waist", "hips"]);

const ChallengeReport = ({
  dateFrom,
  dateTo,
  type = "push",
  programId = null,
  memberView = false,
  previewTemplateId = null,
}) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();
  const {
    challengeReport: { participants = [], templates = [] },
    leaderboardBoards,
    leaderboardPreview,
    activeUser,
    organizationOwner,
    organizationPermissions: globalOrganizationPermissions = [],
    organizationRoles = [],
  } = locals;

  const { userCanAccessToPush, userCanAccessToPress, userCanAccessToPerform } =
    getUserAccess(
      activeUser,
      organizationOwner,
      globalOrganizationPermissions,
      organizationRoles
    );
  const [search, setSearch] = useState("");
  const [photoModal, setPhotoModal] = useState(null);
  const [localDateFrom, setLocalDateFrom] = useState(dateFrom);
  const [localDateTo, setLocalDateTo] = useState(dateTo);
  const [rangeSelector, setRangeSelector] = useState({
    type: null,
    value: null,
  });
  const [quickDisabled, setQuickDisabled] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "totalWorkoutDays",
    direction: "descending",
  });

  const defaultTemplate = templates.find((tpl) => tpl.isDefault);

  const [selectedPrograms, setSelectedPrograms] = useState(
    () => new Set(defaultTemplate?.scope?.teamPrograms ?? [])
  );

  const [selectedColumns, setSelectedColumns] = useState(
    () => new Set(defaultTemplate?.columns ?? DEFAULT_COLUMNS)
  );
  const [selectedMeasurements, setSelectedMeasurements] = useState(
    () => new Set(defaultTemplate?.bodyMeasurements ?? DEFAULT_MEASUREMENTS)
  );
  const [activeRepMaxExercises, setActiveRepMaxExercises] = useState(
    () => defaultTemplate?.repMaxExercises ?? []
  );
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState(
    () => (memberView && previewTemplateId) || defaultTemplate?._id || null
  );
  const [activeColumnRules, setActiveColumnRules] = useState(
    () => defaultTemplate?.columnRules ?? []
  );

  const programOptions = useMemo(
    () =>
      [
        ...new Set(participants.map((p) => p.programTeam).filter(Boolean)),
      ].sort(),
    [participants]
  );

  const filteredParticipants = useMemo(() => {
    let result = participants;
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.name?.toLowerCase().includes(lower) ||
          p.email?.toLowerCase().includes(lower) ||
          p.programTeam?.toLowerCase().includes(lower)
      );
    }
    if (selectedPrograms.size > 0) {
      result = result.filter((p) => selectedPrograms.has(p.programTeam));
    }
    return result;
  }, [participants, search, selectedPrograms]);

  const sortedParticipants = useMemo(() => {
    const sorted = [...filteredParticipants];
    sorted.sort((a, b) => {
      let aVal = getSortValue(a, sortConfig.key);
      let bVal = getSortValue(b, sortConfig.key);
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = (bVal || "").toLowerCase();
      }
      if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredParticipants, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction !== "descending"
          ? "descending"
          : "ascending",
    }));
  };

  const handleLoadTemplate = (templateId) => {
    if (!templateId) {
      setSelectedColumns(new Set(DEFAULT_COLUMNS));
      setSelectedMeasurements(new Set(DEFAULT_MEASUREMENTS));
      setActiveRepMaxExercises([]);
      setSelectedPrograms(new Set());
      setActiveTemplateId(null);
      setActiveColumnRules([]);
      return;
    }
    const template = templates.find(
      (t) => t._id === templateId || String(t._id) === templateId
    );
    if (!template) return;
    setSelectedColumns(new Set(template.columns || []));
    setSelectedMeasurements(new Set(template.bodyMeasurements || []));
    setActiveRepMaxExercises(template.repMaxExercises || []);
    setActiveColumnRules(template.columnRules || []);
    setSelectedPrograms(
      template.scope?.teamPrograms?.length > 0
        ? new Set(template.scope.teamPrograms)
        : new Set()
    );
    setActiveTemplateId(templateId);
  };

  const handleApplyColumns = ({
    columns,
    measurements,
    repMaxExercises,
    columnRules,
  }) => {
    setSelectedColumns(new Set(columns));
    setSelectedMeasurements(new Set(measurements));
    setActiveRepMaxExercises(repMaxExercises || []);
    setActiveColumnRules(columnRules || []);
    setActiveTemplateId(null);
  };

  const handleSaveTemplate = ({
    name,
    columns,
    measurements,
    scopeTeamPrograms,
    repMaxExercises,
    columnRules,
    isDefault,
    existingId,
  }) => {
    submit("/actions/challenge-report-templates", {
      method: "POST",
      body: {
        ...(existingId ? { id: existingId } : {}),
        name,
        isDefault: !!isDefault,
        columns: [...columns],
        bodyMeasurements: [...measurements],
        repMaxExercises: repMaxExercises || [],
        columnRules: columnRules || [],
        scope: { teamPrograms: scopeTeamPrograms || [] },
      },
    });
    // Show the saved settings straight away (the page keeps its state).
    setSelectedColumns(new Set(columns));
    setSelectedMeasurements(new Set(measurements));
    setActiveRepMaxExercises(repMaxExercises || []);
    setActiveColumnRules(columnRules || []);
  };

  const handleRangeUpdate = ({
    dateFrom: df,
    dateTo: dt,
    rangeSelector: rs,
    quickDisabled: qd,
  }) => {
    const newDateFrom =
      df !== undefined
        ? moment(df, "MMM D, YYYY").format("YYYYMMDD")
        : localDateFrom;
    const newDateTo =
      dt !== undefined
        ? dt
          ? moment(dt, "MMM D, YYYY").format("YYYYMMDD")
          : null
        : localDateTo;

    if (df !== undefined) setLocalDateFrom(newDateFrom);
    if (dt !== undefined) setLocalDateTo(newDateTo);
    if (rs !== undefined) setRangeSelector(rs);
    if (qd !== undefined) setQuickDisabled(qd);

    const from = moment(newDateFrom, "YYYYMMDD");
    const to = moment(newDateTo || moment().format("YYYYMMDD"), "YYYYMMDD");
    if (to.diff(from, "days") <= 365) {
      submit("/actions/refresh", {
        method: "GET",
        body: { dateFrom: newDateFrom, dateTo: newDateTo, type },
      });
    }
  };

  const dateRangeExceedsLimit = useMemo(() => {
    const from = moment(localDateFrom, "YYYYMMDD");
    const to = moment(localDateTo || moment().format("YYYYMMDD"), "YYYYMMDD");
    return to.diff(from, "days") > 365;
  }, [localDateFrom, localDateTo]);

  const selectorOptions = useMemo(
    () => [
      {
        key: "2weeks",
        text: t("challengeReport.rangeSelector.twoWeeks", "2 Weeks"),
        value: 2,
        type: "weeks",
        active:
          !quickDisabled &&
          rangeSelector.type === "weeks" &&
          rangeSelector.value === 2,
      },
      {
        key: "1month",
        text: t("challengeReport.rangeSelector.oneMonth", "1 Month"),
        value: 1,
        type: "months",
        active:
          !quickDisabled &&
          rangeSelector.type === "months" &&
          rangeSelector.value === 1,
      },
      {
        key: "3month",
        text: t("challengeReport.rangeSelector.threeMonths", "3 Months"),
        value: 3,
        type: "months",
        active:
          !quickDisabled &&
          rangeSelector.type === "months" &&
          rangeSelector.value === 3,
      },
      {
        key: "6month",
        text: t("challengeReport.rangeSelector.sixMonths", "6 Months"),
        value: 6,
        type: "months",
        active:
          !quickDisabled &&
          rangeSelector.type === "months" &&
          rangeSelector.value === 6,
      },
      {
        key: "1year",
        text: t("challengeReport.rangeSelector.oneYear", "1 Year"),
        value: 1,
        type: "years",
        active:
          !quickDisabled &&
          rangeSelector.type === "years" &&
          rangeSelector.value === 1,
      },
    ],
    [quickDisabled, rangeSelector, t]
  );

  // Audience tabs. "Gym members" = PRESS + PERFORM, one board set.
  const productButtons = [
    {
      key: "pull",
      label: t("challengeReport.products.pull"),
      who: activeUser?.referAsClients
        ? t("challengeReport.products.pullWhoClients", "Coached clients")
        : t("challengeReport.products.pullWhoAthletes", "Coached athletes"),
      show: true,
    },
    {
      key: "push",
      label: t("challengeReport.products.push"),
      who: t("challengeReport.products.pushWho", "Subscribers"),
      show: userCanAccessToPush,
    },
    {
      key: "gym",
      label: t("challengeReport.products.gym", "Gym members"),
      who: null,
      show: userCanAccessToPress || userCanAccessToPerform,
    },
  ].filter((b) => b.show);

  const boards = leaderboardBoards?.boards || [];
  const hasLiveBoard = (audience) =>
    boards.some((b) => b.audience === audience && b.state === "public");
  const liveAudiencesOf = (templateId) => [
    ...new Set(
      boards
        .filter(
          (b) =>
            String(b.templateId) === String(templateId) && b.state === "public"
        )
        .map((b) => t(`challengeReport.publish.tab.${b.audience}`))
    ),
  ];

  // Program filter narrows the board audience when exactly one is selected.
  const programIdByName = useMemo(
    () =>
      new Map(
        participants
          .filter((p) => p.programTeam && p.programId)
          .map((p) => [p.programTeam, p.programId])
      ),
    [participants]
  );
  const programNameById = useMemo(
    () => new Map([...programIdByName].map(([name, id]) => [id, name])),
    [programIdByName]
  );
  const selectedProgramId =
    selectedPrograms.size === 1
      ? programIdByName.get([...selectedPrograms][0]) || null
      : null;
  const userChangedPrograms = useRef(false);
  const handleSetSelectedPrograms = (next) => {
    userChangedPrograms.current = true;
    setSelectedPrograms(next);
  };
  useEffect(() => {
    if (!userChangedPrograms.current) return;
    userChangedPrograms.current = false;
    if ((selectedProgramId || null) !== (programId || null)) {
      submit("/actions/refresh", {
        method: "GET",
        body: { programId: selectedProgramId || "" },
      });
    }
  }, [selectedProgramId, programId]);

  // Member view follows the template picker.
  useEffect(() => {
    if (!memberView || String(activeTemplateId) === String(previewTemplateId)) {
      return;
    }
    submit("/actions/refresh", {
      method: "GET",
      body: activeTemplateId
        ? { templateId: activeTemplateId }
        : { memberView: "" },
    });
  }, [memberView, activeTemplateId, previewTemplateId]);

  const handleSelectBoard = (board) => {
    handleLoadTemplate(String(board.templateId));
    const boardProgramId = board.programId ? String(board.programId) : null;
    if (boardProgramId !== (programId || null)) {
      const programName = programNameById.get(boardProgramId);
      setSelectedPrograms(new Set(programName ? [programName] : []));
      submit("/actions/refresh", {
        method: "GET",
        body: { programId: boardProgramId || "" },
      });
    }
  };

  const activeTemplate = templates.find(
    (tpl) => String(tpl._id) === String(activeTemplateId)
  );

  const units = activeUser?.units || "metric";

  const csvString = useMemo(() => {
    if (!participants.length) return "";
    const fields = ["name", "email", "programTeam"];
    if (selectedColumns.has("workouts")) fields.push("totalWorkoutDays");
    if (selectedColumns.has("completionRate"))
      fields.push("totalScheduledDays");
    if (selectedColumns.has("avgDailySteps")) fields.push("avgDailySteps");
    if (selectedColumns.has("habitsAdherence")) fields.push("habitsAdherence");
    if (selectedColumns.has("totalVolume")) fields.push("totalVolume");
    if (selectedColumns.has("totalReps")) fields.push("totalReps");
    if (selectedColumns.has("totalSets")) fields.push("totalSets");
    if (selectedColumns.has("avgSessionRpe")) fields.push("avgSessionRpe");
    if (selectedColumns.has("avgTrainingReadiness"))
      fields.push("avgTrainingReadiness");
    if (selectedColumns.has("avgWaterIntake")) fields.push("avgWaterIntake");
    if (selectedColumns.has("bodyweight")) {
      fields.push("bodyweight.before", "bodyweight.after", "bodyweight.change");
    }
    if (selectedMeasurements.has("waist")) {
      fields.push("waist.before", "waist.after", "waist.change");
    }
    if (selectedMeasurements.has("hips")) {
      fields.push("hips.before", "hips.after", "hips.change");
    }
    for (const key of selectedMeasurements) {
      if (key !== "waist" && key !== "hips") {
        fields.push(
          `bodyMeasurements.${key}.before`,
          `bodyMeasurements.${key}.after`,
          `bodyMeasurements.${key}.change`
        );
      }
    }

    const loadUnit = units === "imperial" ? "lb" : "kg";
    for (const exercise of activeRepMaxExercises) {
      const fn = getFullName(exercise);
      const reps = exercise.repTarget;
      const colLabel = `${exercise.label || fn} (${reps}RM)`;
      fields.push(
        {
          label: `${colLabel} before (${loadUnit})`,
          value: (row) => {
            const v = row.repMax?.[fn]?.[reps]?.before;
            if (v == null) return null;
            return units === "imperial" ? Math.round(v * 2.20462 * 10) / 10 : v;
          },
        },
        {
          label: `${colLabel} after (${loadUnit})`,
          value: (row) => {
            const v = row.repMax?.[fn]?.[reps]?.after;
            if (v == null) return null;
            return units === "imperial" ? Math.round(v * 2.20462 * 10) / 10 : v;
          },
        },
        {
          label: `${colLabel} gain (${loadUnit})`,
          value: (row) => {
            const v = row.repMax?.[fn]?.[reps]?.gain;
            if (v == null) return null;
            return units === "imperial" ? Math.round(v * 2.20462 * 10) / 10 : v;
          },
        }
      );
    }

    try {
      const parser = new Parser({ fields });
      return parser.parse(participants);
    } catch {
      return "";
    }
  }, [
    participants,
    selectedColumns,
    selectedMeasurements,
    activeRepMaxExercises,
    units,
  ]);

  return (
    <main className="challenge-report">
      <header className="challenge-report__header">
        <h1>{t("challengeReport.pageTitle")}</h1>

        <p className="challenge-report__active-range">
          {t("challengeReport.activeRange")}
        </p>
      </header>

      <div className="challenge-report__product-customize">
        {productButtons.length > 1 && (
          <nav role="tablist">
            {productButtons.map(({ key, label, who }) => (
              <a
                key={key}
                href={`/actions/refresh?dateFrom=${localDateFrom}&dateTo=${localDateTo}&type=${key}&programId=&memberView=`}
                aria-current={type === key || undefined}
              >
                {label}
                {who && (
                  <span className="challenge-report__tab-who">{who}</span>
                )}
                {hasLiveBoard(key) && (
                  <span
                    className="challenge-report__tab-live"
                    title={t(
                      "challengeReport.publish.tabLive",
                      "Has a live leaderboard"
                    )}
                  />
                )}
              </a>
            ))}
          </nav>
        )}

        <div className="challenge-report__toolbar">
          <select
            className="challenge-report__template-select"
            value={activeTemplateId ?? ""}
            onChange={(e) => handleLoadTemplate(e.target.value || null)}
          >
            <option value="">
              {t("challengeReport.toolbar.defaultView", "Default view")}
            </option>
            {templates.map((tpl) => {
              const liveOn = liveAudiencesOf(tpl._id);
              return (
                <option key={tpl._id} value={tpl._id}>
                  {liveOn.length
                    ? `${tpl.name} · ${t("challengeReport.publish.liveOn", "Live: {{audiences}}", { audiences: liveOn.join(", ") })}`
                    : tpl.name}
                </option>
              );
            })}
          </select>
          <button
            type="button"
            data-button-type="primary"
            onClick={() => setShowCustomizeModal(true)}
            data-icon
          >
            <SvgIcon src="/img/new-icons/edit-pencil.svg" noFilterOnLight />
            {t("challengeReport.toolbar.customize", "Customize")}
          </button>
        </div>
      </div>

      <PublishStrip
        type={type}
        programId={programId}
        programName={programId ? programNameById.get(programId) : null}
        boardsData={leaderboardBoards}
        activeTemplate={activeTemplate}
        templates={templates}
        memberView={memberView}
        referAsClients={Boolean(activeUser?.referAsClients)}
        onSelectBoard={handleSelectBoard}
      />

      <div
        className="challenge-report__date-controls"
        aria-disabled={memberView || undefined}
        inert={memberView ? "" : undefined}
      >
        <RangeContainerComponent
          dateFrom={
            localDateFrom
              ? moment(localDateFrom, "YYYYMMDD").format("MMM D, YYYY")
              : null
          }
          dateTo={
            localDateTo
              ? moment(localDateTo, "YYYYMMDD").format("MMM D, YYYY")
              : null
          }
          rangeSelector={rangeSelector}
          quickDisabled={quickDisabled}
          updateState={handleRangeUpdate}
          getDateFirstEntry={() => moment(dateFrom, "YYYYMMDD").toDate()}
          selectorOptions={selectorOptions}
        />
        {dateRangeExceedsLimit && (
          <span className="challenge-report__date-error">
            {t(
              "challengeReport.dateRangeError",
              "Date range cannot exceed 1 year"
            )}
          </span>
        )}
      </div>

      <LeaderCards participants={participants} />

      {memberView ? (
        <MemberPreviewTable
          preview={leaderboardPreview}
          units={units}
          audience={type}
          canHold={Boolean(leaderboardBoards?.canPublish)}
          boardsData={leaderboardBoards}
        />
      ) : (
        <ChallengeTable
          participants={sortedParticipants}
          handleSort={handleSort}
          search={search}
          setSearch={setSearch}
          csvString={csvString}
          onViewPhotos={(participant) => setPhotoModal(participant)}
          programOptions={programOptions}
          selectedPrograms={selectedPrograms}
          setSelectedPrograms={handleSetSelectedPrograms}
          selectedColumns={selectedColumns}
          selectedMeasurements={selectedMeasurements}
          activeRepMaxExercises={activeRepMaxExercises}
          columnRules={activeColumnRules}
        />
      )}

      {participants.length > 0 && !memberView && (
        <ChallengeCharts participants={participants} />
      )}

      {photoModal && (
        <ChallengePhotoCompareModal
          participant={photoModal}
          onClose={(e) => {
            e.preventDefault();
            setPhotoModal(null);
          }}
        />
      )}

      <CustomizeTableModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        selectedColumns={selectedColumns}
        selectedMeasurements={selectedMeasurements}
        initialRepMaxExercises={activeRepMaxExercises}
        initialName={activeTemplate?.name ?? ""}
        initialScopeTeamPrograms={activeTemplate?.scope?.teamPrograms ?? []}
        initialIsDefault={!!activeTemplate?.isDefault}
        initialColumnRules={activeColumnRules}
        liveBoardCount={
          activeTemplate
            ? boards.filter(
                (b) =>
                  String(b.templateId) === String(activeTemplate._id) &&
                  b.state === "public"
              ).length
            : 0
        }
        existingId={activeTemplate?._id}
        programOptions={programOptions}
        onApply={handleApplyColumns}
        onSaveTemplate={handleSaveTemplate}
      />
    </main>
  );
};

const getSortValue = (participant, key) => {
  switch (key) {
    case "name":
      return participant.name;
    case "programTeam":
      return participant.programTeam;
    case "avgDailySteps":
      return participant.avgDailySteps;
    case "totalWorkoutDays":
      return participant.totalWorkoutDays;
    case "habitsAdherence":
      return participant.habitsAdherence;
    case "completionRate":
      return participant.totalScheduledDays
        ? participant.totalWorkoutDays / participant.totalScheduledDays
        : null;
    case "totalVolume":
      return participant.totalVolume;
    case "totalReps":
      return participant.totalReps;
    case "totalSets":
      return participant.totalSets;
    case "avgSessionRpe":
      return participant.avgSessionRpe;
    case "avgTrainingReadiness":
      return participant.avgTrainingReadiness;
    case "avgWaterIntake":
      return participant.avgWaterIntake;
    case "bodyweight.change":
      return participant.bodyweight?.change;
    case "waist.change":
      return participant.waist?.change;
    case "hips.change":
      return participant.hips?.change;
    default: {
      if (key.startsWith("bodyMeasurements.")) {
        const parts = key.split(".");
        return participant.bodyMeasurements?.[parts[1]]?.[parts[2]];
      }
      return participant[key];
    }
  }
};

export default ChallengeReport;
